import styled from '@emotion/styled';
import { Dropdown, Menu } from 'antd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import {
    Link,
    RouteComponentProps,
    useLocation,
    withRouter,
    matchPath,
    match as Match,
} from 'react-router-dom';
import { colors as dsColors, mediaQuery } from '@modernhealth/vital-foundation';
import {
    Button,
    ComponentTextMedium,
    ComponentTextSmall,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';
import { useAnalytics } from '@modernhealth/web-analytics';
import { useMessagingClient, useUnreadConversations } from '@modernhealth/messaging-kit';

import { navigationService } from 'src/config/rootService';
import { Dispatch, RootState } from 'src/config/rootStore';
import {
    getFullName,
    getUserDependentInvitationFeature,
} from 'src/modules/user/selectors/userSelectors';
import { color } from 'src/shared/styles/variables';
import { config as adminConfig } from 'src/modules/admin/config/config';
import { config } from 'src/config/config';
import { Role } from 'src/modules/user/models/user';
import {
    isHrAdminSelector,
    myHrAdminRolePermissionsSelector,
} from 'src/modules/admin/selectors/adminSelectors';
import circlesIcon from 'src/public/img/icon-circles.svg';
import programsIcon from 'src/public/img/icon-course-grey.svg';
import faqIcon from 'src/public/img/icon-faq-grey.svg';
import messagesIcon from 'src/public/img/icon-messages-blue.svg';
import userIcon from 'src/public/img/icon-user-grey.svg';
import { ReactComponent as PieChartIcon } from 'src/public/img/icon-pie-chart.svg';
import { analyticsClient } from 'src/shared/services/analytics';
import { HrAdminPermission } from 'src/generated/webapp_gql';

import { useLocalizedVersion } from '../hooks/useLocalizedVersion';
import { CrisisModal } from '../components/CrisisModal';
import { IndicatorDot } from '../components/IndicatorDot';
import { hexToRgba } from '../helpers/hexToRgba';
import * as S from './styles';
import { HeaderTestId } from './testIds';
import { useDoesUserQualifyForTherapy } from 'src/modules/user/hooks/userHooks';
import {
    AdminResourcesMenu,
    AdminToolsMenu,
    AdminProfileMenu,
    ConditionalPermissionsTooltip,
} from 'src/modules/admin/components/AdminDropdownMenus';
import { NotificationsModal } from 'src/modules/scheduledContent/components/NotificationsModal';
import { BrandImage } from './BrandImage';
import { AdminSearch } from '../../modules/admin/components/AdminSearch';

export const headerInnerId = 'header-inner';

const sectionLinkStyles = ({ alignment = 'left' }: { alignment?: 'left' | 'right' }) => ({
    [alignment === 'left' ? 'marginLeft' : 'marginRight']: pxToRem(30),
    marginTop: pxToRem(6),
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    [mediaQuery.maxWidth('sm')]: {
        marginLeft: pxToRem(6),
    },
});

const SectionLinkWrapper = styled(Link)(sectionLinkStyles);
const SectionExternalLinkWrapper = styled.a(sectionLinkStyles);

const SectionLinkIcon = styled.img({
    marginBottom: pxToRem(2),
    width: pxToRem(16),
    height: pxToRem(16),
});

interface SectionLinkTextProps {
    active?: boolean;
}

const SectionLinkText = styled(ComponentTextSmall)<SectionLinkTextProps>(({ active }) => ({
    marginLeft: `${pxToRem(10)} !important`, // override createTextComponent !important
    color: active ? dsColors.gray65 : color.blueBayoux,
    borderBottom: '1px solid transparent',
    borderColor: active ? '#4C7DE0' : 'transparent',
}));

const MenuItem = styled(Menu.Item)({
    paddingTop: 0,
    paddingBottom: 0,

    '&&:hover': {
        backgroundColor: color.white,
    },
});

interface MenuItemTextProps {
    withBorder?: boolean;
}

const MenuItemText = styled(ComponentTextSmall)<MenuItemTextProps>(({ withBorder }) => ({
    paddingBottom: pxToRem(5),
    paddingTop: pxToRem(10),
    borderBottom: `1px solid ${withBorder ? hexToRgba(color.casper, 0.6) : 'transparent'}`,
    color: dsColors.gray65,
}));

const SectionLinkIndicatorDot = styled(IndicatorDot)({
    marginLeft: pxToRem(5),
});

const MainNavigation = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
});

const AdminInsightsMenu = styled(Button)({
    backgroundColor: 'inherit',
    padding: pxToRem(8, 28),

    '&&:hover': {
        cursor: 'pointer',
        borderRadius: pxToRem(8),
    },
    '&&:focus': {
        boxShadow: 'none',
    },
    '&&:disabled': {
        backgroundColor: 'inherit',
        cursor: 'not-allowed',
        border: 0,
        color: dsColors.gray85,
        opacity: '0.3',
        paddingTop: pxToRem(16),
    },
});

const SectionLink: React.FC<{
    to: string;
    secondaryMatchPaths?: string[];
    iconSrc: string;
    alignment?: 'left' | 'right';
    indicator?: boolean;
}> = ({ to, secondaryMatchPaths = [], iconSrc, alignment, indicator, children, ...props }) => {
    const location = useLocation();

    const paths = [to, ...secondaryMatchPaths];
    let match: Match | null = null;
    for (let i = 0; i < paths.length; i++) {
        match = matchPath(location.pathname, {
            path: paths[i],
            exact: false,
        });

        if (match) {
            break;
        }
    }

    const activeDomain = !!match;
    // We do not want to indicate that the page is active if the match
    // path isn't the original link. There can be an exact match
    // without it matching the original link because of the secondary
    // match paths we can define.
    const activePage = match ? match.isExact && match.path === to : false;

    return (
        <SectionLinkWrapper
            {...props}
            to={to}
            aria-current={activePage ? 'page' : activeDomain ? 'location' : false}
            alignment={alignment}
        >
            <SectionLinkIcon src={iconSrc} />
            <SectionLinkText active={activeDomain}>{children}</SectionLinkText>
            {indicator && (
                <SectionLinkIndicatorDot data-testid={HeaderTestId.SectionLinkIndicator} />
            )}
        </SectionLinkWrapper>
    );
};

const SectionExternalLink: React.FC<{
    to: string;
    iconSrc: string;
    alignment?: 'left' | 'right';
}> = ({ to, iconSrc, alignment, children }) => {
    return (
        <SectionExternalLinkWrapper href={to} target="_blank" alignment={alignment}>
            <SectionLinkIcon src={iconSrc} />
            <SectionLinkText>{children}</SectionLinkText>
        </SectionExternalLinkWrapper>
    );
};

interface StyledPieChartIconProps {
    disabled: boolean;
}

const StyledPieChartIcon = styled(PieChartIcon)(({ disabled }: StyledPieChartIconProps) => ({
    color: disabled ? dsColors.gray85 : dsColors.blue55,
}));

type Props = {
    userRole: Role;
} & ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    RouteComponentProps;

const HeaderContainer: React.FC<Props> = props => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const { fullName, userRole, showCrisisModal } = props;
    const { pathname } = useLocation();
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const messagingClient = useMessagingClient();
    const { unreadConversations } = useUnreadConversations();
    const [localizedVersion] = useLocalizedVersion();
    const {
        releaseDiginShowDailyPauseCalendarRemindersWeb,
        releaseDiginShowDailyPauseNotificationsWeb,
        showCirclesInMemberWeb,
        adminPortalPermissionsV1Enabled,
        clientPortalUnification,
    } = useFlags();

    const qualifiesForTherapy = useDoesUserQualifyForTherapy();
    const myHrAdminRolePermissions = useSelector(myHrAdminRolePermissionsSelector);
    const isHrAdmin = useSelector(isHrAdminSelector);
    const hasAdminPermissions =
        (!adminPortalPermissionsV1Enabled && isHrAdmin) ||
        (adminPortalPermissionsV1Enabled && myHrAdminRolePermissions.length > 0);
    const canSeeInsights =
        !adminPortalPermissionsV1Enabled ||
        myHrAdminRolePermissions.includes(HrAdminPermission.InsightsView);
    const dailyPauseSettingsEnabled =
        releaseDiginShowDailyPauseCalendarRemindersWeb ||
        releaseDiginShowDailyPauseNotificationsWeb;

    const redirect = (url: string) => () => setTimeout(() => navigationService.navigate(url)); // workaround => react-select used by antd doesn't support react.lazy yet

    const isCoach = userRole === Role.COACH;
    const isAdmin = pathname.includes('admin');
    const acceptedAdminTerms = localStorage.getItem(adminConfig.termsLocalStorageKey);
    const isAdminTerms = isAdmin && !acceptedAdminTerms;
    const dependentInvitationFeature = useSelector(getUserDependentInvitationFeature);

    const onLogOut = () => {
        analyticsClient.reset();
        props.logout();
    };

    useEffect(() => {
        messagingClient.startWebSocketSubscriptions();
    }, [messagingClient]);

    const renderLinks = () => (
        <>
            <SectionLink
                to="/resources"
                secondaryMatchPaths={['/programs', '/meditations']}
                iconSrc={programsIcon}
            >
                {t('shared:header.explore')}
            </SectionLink>

            {(() => {
                if (showCirclesInMemberWeb) {
                    return (
                        <SectionLink to="/circles" iconSrc={circlesIcon}>
                            {t('shared:header.circles')}
                        </SectionLink>
                    );
                }

                if (!localizedVersion) {
                    return (
                        <SectionExternalLink
                            to="https://circles.modernhealth.com/?utm_source=modern-health&utm_medium=web-app"
                            iconSrc={circlesIcon}
                        >
                            {t('shared:header.circles')}
                        </SectionExternalLink>
                    );
                }

                return null;
            })()}

            {!localizedVersion && (
                <SectionExternalLink to={config.faqUrl} iconSrc={faqIcon}>
                    {t('shared:header.faq')}
                </SectionExternalLink>
            )}
        </>
    );

    const isOnMainAdminPage = pathname === '/admin';
    const renderAdminNavBar = () => {
        return (
            <>
                <MainNavigation>
                    <Link data-testid="HeaderTestID.BrandImage" to="/admin">
                        <BrandImage />
                    </Link>
                    <ConditionalPermissionsTooltip
                        trigger={
                            <AdminInsightsMenu
                                iconAlign="start"
                                variant="flat"
                                disabled={!canSeeInsights}
                                onClick={() => {
                                    analytics.trackClick('admin portal navigation bar', {
                                        translation_key: 'admin:menu.insights.button.title',
                                        destination: '/admin/reporting',
                                        page_section: 'navigation bar',
                                    });
                                    navigationService.navigate('/admin/reporting');
                                }}
                            >
                                <StyledPieChartIcon
                                    aria-label="pie chart"
                                    disabled={!canSeeInsights}
                                />
                                <ComponentTextMedium>
                                    {t('admin:menu.insights.button.title')}
                                </ComponentTextMedium>
                            </AdminInsightsMenu>
                        }
                        disabled={!canSeeInsights}
                    >
                        {t('admin:admin_permissions_tooltip.hover.insufficient_permissions')}
                    </ConditionalPermissionsTooltip>

                    <AdminResourcesMenu />
                    <AdminToolsMenu />
                </MainNavigation>
                {clientPortalUnification && (
                    <MainNavigation>
                        <AdminSearch />
                    </MainNavigation>
                )}
                <MainNavigation>
                    <AdminProfileMenu onLogOut={onLogOut} />
                </MainNavigation>
            </>
        );
    };

    if (isAdmin) {
        return (
            <S.AppHeader
                className="simplifiedForm-header loggedIn"
                data-testid="appHeader"
                backgroundColor={isOnMainAdminPage ? dsColors.blue05 : dsColors.white}
            >
                <S.HeaderInnerWrapper id={headerInnerId} className="header-inner">
                    {renderAdminNavBar()}
                </S.HeaderInnerWrapper>
            </S.AppHeader>
        );
    }

    return (
        <S.AppHeader className="simplifiedForm-header loggedIn" data-testid="appHeader">
            <S.HeaderInnerWrapper className="header-inner">
                <MainNavigation>
                    <Link data-testid="HeaderTestID.BrandImage" to="/">
                        <BrandImage />
                    </Link>
                    {!isAdmin && renderLinks()}
                </MainNavigation>

                <MainNavigation>
                    {!isAdmin && (
                        <SectionLink
                            to="/messages"
                            indicator={unreadConversations.length > 0}
                            aria-label={
                                unreadConversations.length > 0
                                    ? t('shared:header.messages.unread')
                                    : t('shared:header.messages')
                            }
                            alignment="right"
                            iconSrc={messagesIcon}
                        >
                            {t('shared:header.messages')}
                        </SectionLink>
                    )}
                    {!isAdminTerms && fullName.length > 1 && (
                        <S.UserActionsWrapper
                            className="userActions-wrapper"
                            data-testid={HeaderTestId.UserActions}
                        >
                            <Dropdown
                                overlay={
                                    <Menu>
                                        {!isAdmin && !isCoach && (
                                            <MenuItem key="0" onClick={redirect('/profile')}>
                                                <MenuItemText withBorder>
                                                    {t('shared:header.profile')}
                                                </MenuItemText>
                                            </MenuItem>
                                        )}
                                        {hasAdminPermissions && (
                                            <MenuItem
                                                key="1"
                                                onClick={redirect(
                                                    isAdmin ? '/dashboard' : '/admin',
                                                )}
                                            >
                                                <MenuItemText withBorder>
                                                    {t(
                                                        isAdmin
                                                            ? 'admin:switch_to_member_view'
                                                            : 'admin:switch_to_admin_view',
                                                    )}
                                                </MenuItemText>
                                            </MenuItem>
                                        )}
                                        {!isAdmin && qualifiesForTherapy ? (
                                            <MenuItem
                                                key="2"
                                                onClick={redirect('/work-with-a-therapist')}
                                            >
                                                <MenuItemText withBorder>
                                                    {t('shared:header.request_therapy')}
                                                </MenuItemText>
                                            </MenuItem>
                                        ) : null}

                                        {!isAdmin && dependentInvitationFeature ? (
                                            <MenuItem
                                                key="3"
                                                onClick={() => redirect('/invite-dependent')()}
                                            >
                                                <MenuItemText withBorder>
                                                    {t('shared:header.invite_dependent')}
                                                </MenuItemText>
                                            </MenuItem>
                                        ) : null}

                                        {!isAdmin && dailyPauseSettingsEnabled && (
                                            <MenuItem
                                                key="4"
                                                onClick={() => {
                                                    setShowNotificationsModal(true);
                                                }}
                                            >
                                                <MenuItemText withBorder>
                                                    {t('shared:header.notifications')}
                                                </MenuItemText>
                                            </MenuItem>
                                        )}

                                        {!isAdmin && (
                                            <MenuItem
                                                key="5"
                                                onClick={() => {
                                                    window.open(
                                                        config.faqSubmitRequestUrl,
                                                        '_blank',
                                                    );
                                                }}
                                            >
                                                <MenuItemText withBorder>
                                                    {t('shared:header.contact_us')}
                                                </MenuItemText>
                                            </MenuItem>
                                        )}
                                        {!isAdmin && (
                                            <MenuItem
                                                key="6"
                                                onClick={() => {
                                                    props.toogleShowCrisisModal();
                                                }}
                                            >
                                                <MenuItemText
                                                    css={{ color: color.wildWatermelon }}
                                                    withBorder
                                                >
                                                    {t('shared:header.crisis_info')}
                                                </MenuItemText>
                                            </MenuItem>
                                        )}
                                        <MenuItem
                                            key="7"
                                            onClick={onLogOut}
                                            data-testid={HeaderTestId.LogOut}
                                        >
                                            <MenuItemText>
                                                {t('shared:header.log_out')}
                                            </MenuItemText>
                                        </MenuItem>
                                    </Menu>
                                }
                                trigger={['click']}
                            >
                                <S.UsernameWrapper>
                                    <img src={userIcon} alt="user icon" />
                                    <S.UserName>{fullName}</S.UserName>
                                    <Icon name="triangle-down" />
                                </S.UsernameWrapper>
                            </Dropdown>
                        </S.UserActionsWrapper>
                    )}
                </MainNavigation>
            </S.HeaderInnerWrapper>

            <CrisisModal
                visible={!!showCrisisModal}
                onClose={() => {
                    props.toogleShowCrisisModal();
                }}
                source={props.crisisModalSource}
            />
            {dailyPauseSettingsEnabled && (
                <NotificationsModal
                    isVisible={showNotificationsModal}
                    onClose={() => {
                        setShowNotificationsModal(false);
                    }}
                />
            )}
        </S.AppHeader>
    );
};

const mapStateToProps = (state: RootState) => ({
    fullName: getFullName(state),
    showCrisisModal: state.user.showCrisisModal,
    crisisModalSource: state.user.crisisModalSource,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    logout: dispatch.authentication.logout,
    toogleShowCrisisModal: dispatch.user.toogleShowCrisisModal,
});

export const Header = connect(mapStateToProps, mapDispatchToProps)(withRouter(HeaderContainer));
